import { UnlockOutlined } from '@ant-design/icons';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { ACard, ACheckBoxGroup, AModal } from '../../../components';
import { useConfiguration } from '../../../hooks/configuration';
import { useToast } from '../../../hooks/toast';
import { getPermission, patchPermission, postPermission } from '../../../http';
import {
  PERMISSIONS_CLIENTS,
  PERMISSIONS_COMPANY,
  PERMISSIONS_COVENANTS,
  PERMISSIONS_CRC,
  PERMISSIONS_CRM,
  PERMISSIONS_DASHBOARD,
  PERMISSIONS_DOCUMENTS,
  PERMISSIONS_FINANCIALS,
  PERMISSIONS_FORMS,
  PERMISSIONS_GUIDES,
  PERMISSIONS_HEALTH_CERTIFICATES,
  PERMISSIONS_INVENTORY,
  PERMISSIONS_ODONTOLOGY,
  PERMISSIONS_PROCEDURES,
  PERMISSIONS_PROFESSIONALS,
  PERMISSIONS_SCHEDULES,
  PERMISSIONS_TREATMENTS,
  PERMISSIONS_USERS,
  PERMISSIONS_WHATSAPP,
} from '../../../shared/enums/permissions.enum';
import {
  CheckboxValueType,
  IAccessGroup,
  IPermission,
} from '../../../shared/interfaces';
import { hasAllowedModule } from '../../../shared/utils/modules';

import {
  PermissionSectionCount,
  PermissionSectionDescription,
  PermissionSectionHeader,
  PermissionsGrid,
  PermissionsLegend,
  PermissionsLegendItem,
  PermissionsSummary,
  PermissionsSummaryActions,
  PermissionsSummaryHeader,
  ContainerPermissions,
} from './styles';
import {
  schemaPermissionsCreate,
  schemaPermissionsEdit,
} from '../schemas/yup.permissions.schema';

interface IAccessGroupPermissionsProps {
  open: boolean;
  close: () => void;
  accessGroup: IAccessGroup | null;
}

interface IOptions {
  label: string;
  value: string;
}

interface IPermissionSection {
  key: string;
  title: string;
  description: string;
  value: CheckboxValueType[];
  options: IOptions[];
  setValue: (value: CheckboxValueType[]) => void;
}

const formatPermissionLabel = ({ label, value }: IOptions): IOptions => {
  const match = label.match(/^\[(.*?)\]\s*(.*)$/);

  if (!match) {
    return { label, value };
  }

  const moduleName = match[1].trim();
  const action = match[2].trim().toLowerCase();

  const actionMap: Record<string, string> = {
    tela: `Acessar ${moduleName.toLowerCase()}`,
    cadastro: `Cadastrar ${moduleName.toLowerCase()}`,
    alteração: `Editar ${moduleName.toLowerCase()}`,
    pagar: 'Baixar ou receber lançamentos',
    cancelar: 'Cancelar lançamentos',
    permissões: 'Gerenciar permissões do grupo',
    responder: 'Responder prontuários',
    'resetar senha': 'Resetar senha de usuários',
    'resetar senha de ass.': 'Resetar senha de assinatura',
    'cadastro de entrada': 'Registrar entradas no estoque',
    'cadastro de saída': 'Registrar saídas no estoque',
  };

  return {
    label:
      actionMap[action] || `${match[2].trim()} ${moduleName.toLowerCase()}`,
    value,
  };
};

const formatPermissionOptions = (options: IOptions[]) =>
  options.map(formatPermissionLabel);

const getMixedOptionModuleKeys = (value: string): string[] => {
  if (
    value === 'specialties-create' ||
    value === 'specialties-update' ||
    value === 'specialties-view'
  ) {
    return ['especialidades'];
  }

  if (
    value === 'terminologies-create' ||
    value === 'terminologies-update' ||
    value === 'terminologies-view'
  ) {
    return ['convenios'];
  }

  if (value.startsWith('professionals') || value === 'professionals-view') {
    return ['profissionais'];
  }

  if (value.startsWith('users') || value === 'users-view') {
    return ['usuarios'];
  }

  if (value.startsWith('access-groups') || value === 'access-groups-view') {
    return ['permissoes'];
  }

  if (
    value.startsWith('products-categories') ||
    value.startsWith('products-') ||
    value === 'products-view' ||
    value === 'products-categories-view' ||
    value === 'quick-sale-view' ||
    value === 'sales-view'
  ) {
    return ['produtos'];
  }

  if (value.startsWith('inventories-') || value === 'inventories-view') {
    return ['estoque'];
  }

  if (value.startsWith('crm-')) {
    return ['crm'];
  }

  return [];
};

const AccessGroupPermissions: React.FC<IAccessGroupPermissionsProps> = ({
  open,
  close,
  accessGroup,
}) => {
  const { addToast } = useToast();
  const { configuration } = useConfiguration();
  const [permissions, setPermissions] = useState<IPermission | null>(null);
  const [dashboard, setDashboard] = useState<CheckboxValueType[]>([]);
  const [schedules, setSchedules] = useState<CheckboxValueType[]>([]);
  const [odontology, setOdontology] = useState<CheckboxValueType[]>([]);
  const [clients, setClients] = useState<CheckboxValueType[]>([]);
  const [treatments, setTreatments] = useState<CheckboxValueType[]>([]);
  const [healthCertificates, setHealthCertificates] = useState<
    CheckboxValueType[]
  >([]);
  const [documents, setDocuments] = useState<CheckboxValueType[]>([]);
  const [forms, setForms] = useState<CheckboxValueType[]>([]);
  const [procedures, setProcedures] = useState<CheckboxValueType[]>([]);
  const [financials, setFinancials] = useState<CheckboxValueType[]>([]);
  const [guides, setGuides] = useState<CheckboxValueType[]>([]);
  const [professionals, setProfessionals] = useState<CheckboxValueType[]>([]);
  const [covenants, setCovenants] = useState<CheckboxValueType[]>([]);
  const [users, setUsers] = useState<CheckboxValueType[]>([]);
  const [company, setCompany] = useState<CheckboxValueType[]>([]);
  const [inventory, setInventory] = useState<CheckboxValueType[]>([]);
  const [whatsapp, setWhatsapp] = useState<CheckboxValueType[]>([]);
  const [crm, setCrm] = useState<CheckboxValueType[]>([]);
  const [crc, setCrc] = useState<CheckboxValueType[]>([]);

  const clearAllSelections = useCallback(() => {
    setPermissions(null);
    setDashboard([]);
    setSchedules([]);
    setClients([]);
    setOdontology([]);
    setTreatments([]);
    setHealthCertificates([]);
    setDocuments([]);
    setForms([]);
    setProcedures([]);
    setFinancials([]);
    setGuides([]);
    setProfessionals([]);
    setCovenants([]);
    setUsers([]);
    setCompany([]);
    setInventory([]);
    setWhatsapp([]);
    setCrm([]);
    setCrc([]);
  }, []);

  const filterPermissions = useCallback(
    (arrayString: string, values: IOptions[]) => {
      const array = arrayString.split(',');
      const result: string[] = [];
      const filter = values.map((val) => val.value);

      array.forEach((item) => {
        if (filter.includes(item)) {
          result.push(item);
        }
      });

      return result;
    },
    [],
  );

  const loadPermissions = useCallback(async () => {
    if (!accessGroup?.id) {
      clearAllSelections();
      return;
    }

    try {
      const data = await getPermission(accessGroup.id);

      if (!data?.id) {
        clearAllSelections();
        return;
      }

      const { value } = data;
      setPermissions(data);
      setDashboard(filterPermissions(value, PERMISSIONS_DASHBOARD));
      setSchedules(filterPermissions(value, PERMISSIONS_SCHEDULES));
      setClients(filterPermissions(value, PERMISSIONS_CLIENTS));
      setOdontology(filterPermissions(value, PERMISSIONS_ODONTOLOGY));
      setTreatments(filterPermissions(value, PERMISSIONS_TREATMENTS));
      setHealthCertificates(
        filterPermissions(value, PERMISSIONS_HEALTH_CERTIFICATES),
      );
      setDocuments(filterPermissions(value, PERMISSIONS_DOCUMENTS));
      setForms(filterPermissions(value, PERMISSIONS_FORMS));
      setProcedures(filterPermissions(value, PERMISSIONS_PROCEDURES));
      setFinancials(filterPermissions(value, PERMISSIONS_FINANCIALS));
      setGuides(filterPermissions(value, PERMISSIONS_GUIDES));
      setProfessionals(filterPermissions(value, PERMISSIONS_PROFESSIONALS));
      setCovenants(filterPermissions(value, PERMISSIONS_COVENANTS));
      setUsers(filterPermissions(value, PERMISSIONS_USERS));
      setCompany(filterPermissions(value, PERMISSIONS_COMPANY));
      setInventory(filterPermissions(value, PERMISSIONS_INVENTORY));
      setWhatsapp(filterPermissions(value, PERMISSIONS_WHATSAPP));
      setCrm(filterPermissions(value, PERMISSIONS_CRM));
      setCrc(filterPermissions(value, PERMISSIONS_CRC));
    } catch {
      clearAllSelections();
    }
  }, [accessGroup?.id, clearAllSelections, filterPermissions]);

  const sections = useMemo<IPermissionSection[]>(
    () => [
      {
        key: 'dashboard',
        title: 'Visao Geral',
        description: 'Libera o acesso a tela inicial do sistema.',
        value: dashboard,
        setValue: setDashboard,
        options: PERMISSIONS_DASHBOARD,
      },
      {
        key: 'schedules',
        title: 'Agenda',
        description:
          'Libera agenda, status da agenda e feriados para a equipe.',
        value: schedules,
        setValue: setSchedules,
        options: PERMISSIONS_SCHEDULES,
      },
      {
        key: 'clients',
        title: 'Clientes',
        description:
          'Controla o acesso ao cadastro de clientes e fornecedores.',
        value: clients,
        setValue: setClients,
        options: PERMISSIONS_CLIENTS,
      },
      {
        key: 'odontology',
        title: 'Odontologia',
        description: 'Libera os atendimentos odontológicos e suas edições.',
        value: odontology,
        setValue: setOdontology,
        options: PERMISSIONS_ODONTOLOGY,
      },
      {
        key: 'treatments',
        title: 'Tratamentos e orçamentos',
        description: 'Define quem pode acessar, criar e editar tratamentos.',
        value: treatments,
        setValue: setTreatments,
        options: PERMISSIONS_TREATMENTS,
      },
      {
        key: 'health-certificates',
        title: 'Atestados',
        description: 'Permite trabalhar com modelos de atestados e emissões.',
        value: healthCertificates,
        setValue: setHealthCertificates,
        options: PERMISSIONS_HEALTH_CERTIFICATES,
      },
      {
        key: 'documents',
        title: 'Documentos',
        description: 'Controla documentos, receituários e seus modelos.',
        value: documents,
        setValue: setDocuments,
        options: PERMISSIONS_DOCUMENTS,
      },
      {
        key: 'forms',
        title: 'Prontuários',
        description: 'Libera modelos, preenchimento e resposta de prontuários.',
        value: forms,
        setValue: setForms,
        options: PERMISSIONS_FORMS,
      },
      {
        key: 'procedures',
        title: 'Procedimentos',
        description: 'Define quem acessa e mantém a lista de procedimentos.',
        value: procedures,
        setValue: setProcedures,
        options: PERMISSIONS_PROCEDURES,
      },
      {
        key: 'financials',
        title: 'Financeiro',
        description:
          'Inclui contas, formas de pagamento, taxas e controle de caixa.',
        value: financials,
        setValue: setFinancials,
        options: PERMISSIONS_FINANCIALS,
      },
      {
        key: 'guides',
        title: 'Guias',
        description: 'Libera status de guias e o cadastro das próprias guias.',
        value: guides,
        setValue: setGuides,
        options: PERMISSIONS_GUIDES,
      },
      {
        key: 'professionals',
        title: 'Profissionais',
        description: 'Controla profissionais, especialidades e terminologias.',
        value: professionals,
        setValue: setProfessionals,
        options: PERMISSIONS_PROFESSIONALS,
      },
      {
        key: 'covenants',
        title: 'Convênios',
        description: 'Libera o acesso ao cadastro e edição de convênios.',
        value: covenants,
        setValue: setCovenants,
        options: PERMISSIONS_COVENANTS,
      },
      {
        key: 'users',
        title: 'Usuários e grupos',
        description: 'Define acesso a usuários, grupos de acesso e permissões.',
        value: users,
        setValue: setUsers,
        options: PERMISSIONS_USERS,
      },
      {
        key: 'inventory',
        title: 'Estoque e vendas',
        description:
          'Inclui produtos, estoque, venda de produtos e histórico de vendas.',
        value: inventory,
        setValue: setInventory,
        options: PERMISSIONS_INVENTORY,
      },
      {
        key: 'whatsapp',
        title: 'WhatsApp',
        description:
          'Libera o acesso às conexões e ao monitoramento do WhatsApp.',
        value: whatsapp,
        setValue: setWhatsapp,
        options: PERMISSIONS_WHATSAPP,
      },
      {
        key: 'crm',
        title: 'Relacionamento (CRM)',
        description:
          'Controla campanhas, templates de mensagens e disparos via WhatsApp.',
        value: crm,
        setValue: setCrm,
        options: PERMISSIONS_CRM,
      },
      {
        key: 'crc',
        title: 'Relacionamento (CRC)',
        description:
          'Controla dashboard, central de contatos, pendências e relatórios do CRC.',
        value: crc,
        setValue: setCrc,
        options: PERMISSIONS_CRC,
      },
      {
        key: 'company',
        title: 'Dados da empresa',
        description: 'Controla acesso às informações principais da empresa.',
        value: company,
        setValue: setCompany,
        options: PERMISSIONS_COMPANY,
      },
    ],
    [
      clients,
      company,
      covenants,
      crc,
      crm,
      dashboard,
      documents,
      financials,
      forms,
      guides,
      healthCertificates,
      inventory,
      odontology,
      procedures,
      professionals,
      schedules,
      treatments,
      users,
      whatsapp,
    ],
  );

  const visibleSections = useMemo(() => {
    const allowedModules = configuration?.allowed_modules;

    const isModuleVisible = (moduleKey: string) => {
      if (!allowedModules || allowedModules.length === 0) {
        return true;
      }

      return hasAllowedModule(allowedModules, moduleKey);
    };

    const sectionModuleMap: Record<string, string> = {
      schedules: 'agenda',
      clients: 'clientes',
      odontology: 'odontologico',
      treatments: 'tratamentos',
      'health-certificates': 'atestados_medicos',
      documents: 'documentos',
      forms: 'formularios',
      procedures: 'procedimentos',
      financials: 'financeiro',
      guides: 'guias',
      covenants: 'convenios',
      whatsapp: 'whatsapp',
      crm: 'crm',
      crc: 'crc',
      company: 'empresa',
    };

    return sections
      .map((section) => {
        if (section.key === 'dashboard') {
          return section;
        }

        if (
          section.key === 'professionals' ||
          section.key === 'users' ||
          section.key === 'inventory'
        ) {
          return {
            ...section,
            options: section.options.filter((option) =>
              getMixedOptionModuleKeys(option.value).some(isModuleVisible),
            ),
          };
        }

        const moduleKey = sectionModuleMap[section.key];

        if (!moduleKey || isModuleVisible(moduleKey)) {
          return section;
        }

        return {
          ...section,
          options: [],
        };
      })
      .filter((section) => section.options.length > 0);
  }, [configuration?.allowed_modules, sections]);

  const getVisibleSelectedValues = useCallback(
    (section: IPermissionSection) => {
      return section.value.filter((value) =>
        section.options.some((option) => option.value === value),
      );
    },
    [],
  );

  const getSelectedPermissions = useCallback(() => {
    return visibleSections.flatMap((section) =>
      getVisibleSelectedValues(section),
    );
  }, [getVisibleSelectedValues, visibleSections]);

  const selectAllPermissions = useCallback(() => {
    visibleSections.forEach((section) => {
      section.setValue(section.options.map((option) => option.value));
    });
  }, [visibleSections]);

  const clearVisibleSelections = useCallback(() => {
    visibleSections.forEach((section) => {
      section.setValue([]);
    });
  }, [visibleSections]);

  const handleCreate = useCallback(async () => {
    try {
      const validated = await schemaPermissionsCreate.validate({
        access_group_id: accessGroup?.id,
        value: getSelectedPermissions().join(','),
      });

      const data = await postPermission(validated);
      setPermissions(data);

      addToast({
        type: 'success',
        title: 'Permissões salvas',
        description: 'As permissões do grupo foram cadastradas com sucesso.',
      });

      close();
    } catch (error: any) {
      addToast({
        type: 'error',
        title: 'Erro ao salvar permissões',
        description:
          error?.errors?.[0] || 'Não foi possível salvar as permissões.',
      });
    }
  }, [accessGroup?.id, addToast, close, getSelectedPermissions]);

  const handleEdit = useCallback(async () => {
    try {
      const validated = await schemaPermissionsEdit.validate({
        value: getSelectedPermissions().join(','),
      });

      const data = await patchPermission(permissions?.id, validated);
      setPermissions(data);

      addToast({
        type: 'success',
        title: 'Permissões atualizadas',
        description: 'As permissões do grupo foram atualizadas com sucesso.',
      });

      close();
    } catch (error: any) {
      addToast({
        type: 'error',
        title: 'Erro ao atualizar permissões',
        description:
          error?.response?.data?.error ||
          error?.errors?.[0] ||
          'Não foi possível atualizar as permissões.',
      });
    }
  }, [addToast, close, getSelectedPermissions, permissions?.id]);

  const visibleTotalSelected = visibleSections.reduce(
    (sum, section) => sum + getVisibleSelectedValues(section).length,
    0,
  );

  const visibleTotalAvailable = visibleSections.reduce(
    (sum, section) => sum + section.options.length,
    0,
  );

  useEffect(() => {
    if (open) {
      loadPermissions();
    }
  }, [loadPermissions, open]);

  return (
    <AModal
      open={open}
      close={close}
      width={1120}
      onOk={permissions?.id ? handleEdit : handleCreate}
      okText="Salvar permissões"
      title={
        <>
          <UnlockOutlined /> Permissões do grupo
        </>
      }
    >
      <ContainerPermissions>
        <PermissionsSummary>
          <PermissionsSummaryHeader>
            <strong>
              {accessGroup?.description || 'Grupo selecionado'} com{' '}
              {visibleTotalSelected} de {visibleTotalAvailable} permissões
              marcadas
            </strong>
            <PermissionsSummaryActions>
              <button type="button" onClick={selectAllPermissions}>
                Marcar tudo
              </button>
              <button type="button" onClick={clearVisibleSelections}>
                Desmarcar tudo
              </button>
            </PermissionsSummaryActions>
          </PermissionsSummaryHeader>
          <p>
            Marque apenas o que a equipe realmente precisa usar. Em geral,
            “Acessar” libera a tela, “Cadastrar” cria novos registros e “Editar”
            altera os já existentes.
          </p>
          <PermissionsLegend>
            <PermissionsLegendItem>
              <strong>Acessar</strong>
              <span>
                Permite abrir a tela e consultar as informações do módulo.
              </span>
            </PermissionsLegendItem>
            <PermissionsLegendItem>
              <strong>Cadastrar</strong>
              <span>Permite incluir novos registros naquele módulo.</span>
            </PermissionsLegendItem>
            <PermissionsLegendItem>
              <strong>Editar</strong>
              <span>Permite alterar dados já cadastrados.</span>
            </PermissionsLegendItem>
          </PermissionsLegend>
        </PermissionsSummary>

        <PermissionsGrid>
          {visibleSections.map((section) => {
            const selectedValues = getVisibleSelectedValues(section);
            const allSelected =
              selectedValues.length === section.options.length;

            return (
              <ACard
                key={section.key}
                title={
                  <PermissionSectionHeader>
                    <span>{section.title}</span>
                    <PermissionSectionCount
                      onClick={() => {
                        section.setValue(
                          allSelected
                            ? []
                            : section.options.map((option) => option.value),
                        );
                      }}
                      title={
                        allSelected
                          ? 'Desmarcar todos deste módulo'
                          : 'Marcar todos deste módulo'
                      }
                    >
                      {selectedValues.length}/{section.options.length}
                    </PermissionSectionCount>
                  </PermissionSectionHeader>
                }
              >
                <PermissionSectionDescription>
                  {section.description}
                </PermissionSectionDescription>
                <ACheckBoxGroup
                  value={selectedValues}
                  onChange={(value) => section.setValue(value)}
                  options={formatPermissionOptions(section.options)}
                />
              </ACard>
            );
          })}
        </PermissionsGrid>
      </ContainerPermissions>
    </AModal>
  );
};

export default AccessGroupPermissions;
